import React from 'react';
import PropTypes from 'prop-types';
import merge from 'lodash.merge';
import { ClassNames, Global } from '@emotion/react';
import { useTheme } from '@mui/material/styles';

/*
 *  Material-UI does not yet support ability to grab props within style()
 *  https://github.com/mui-org/material-ui/issues/7633
 *
 *  This is a workaround provided from the thread
 */

const styles = (theme, props, style) => {
  return typeof style === 'function' ? style(theme, props) : style;
};

const muiStateClasses = {
  checked: 'Mui-checked',
  disabled: 'Mui-disabled',
  error: 'Mui-error',
  expanded: 'Mui-expanded',
  focusVisible: 'Mui-focusVisible',
  focused: 'Mui-focused',
  panelSummaryExpanded: 'Mui-expanded',
  panelSummaryFocused: 'Mui-focusVisible',
  selected: 'Mui-selected',
  tabSelected: 'Mui-selected',
};

const normalizeSelector = (selector, ruleName, getClassName) => {
  const referencedRuleNames = [];
  const normalizedSelector = selector.replace(/\$([A-Za-z0-9_-]+)/g, (match, referencedRuleName) => {
    referencedRuleNames.push(referencedRuleName);
    return `.${getClassName(referencedRuleName)}`;
  });

  const muiStateSelectors = referencedRuleNames
    .filter(referencedRuleName => muiStateClasses[referencedRuleName])
    .map(referencedRuleName => selector.replace(`$${referencedRuleName}`, `.${muiStateClasses[referencedRuleName]}`).replace(/^&(?=\.)/, '&&'));

  return muiStateSelectors.length ? `${normalizedSelector}, ${muiStateSelectors.join(', ')}` : normalizedSelector;
};

const isStyleSelector = key => {
  return (
    key.startsWith('@') ||
    key.startsWith('&') ||
    key.startsWith('.') ||
    key.startsWith('#') ||
    key.startsWith(':') ||
    key.startsWith('>') ||
    key.startsWith('+') ||
    key.startsWith('~') ||
    key.startsWith('[') ||
    key.indexOf(' ') > -1 ||
    key.indexOf(',') > -1
  );
};

const normalizePropertyName = key => {
  if (key.startsWith('--') || key.indexOf('-') === -1 || isStyleSelector(key)) return key;

  const normalizedKey = key.replace(/^-ms-/, 'ms-').replace(/^-(webkit|moz|o)-/, (match, vendor) => vendor.charAt(0).toUpperCase() + vendor.slice(1) + '-');
  return normalizedKey.replace(/-([a-z])/g, (match, char) => char.toUpperCase());
};

const normalizeStyleObject = (styleObject, ruleName, getClassName) => {
  if (!styleObject || typeof styleObject !== 'object' || Array.isArray(styleObject)) return styleObject;

  return Object.keys(styleObject).reduce((acc, key) => {
    const normalizedKey = key.indexOf('$') > -1 ? normalizeSelector(key, ruleName, getClassName) : normalizePropertyName(key);
    acc[normalizedKey] = normalizeStyleObject(styleObject[key], ruleName, getClassName);
    return acc;
  }, {});
};

const createClasses = (css, styleRules, options) => {
  const ruleNames = Object.keys(styleRules).filter(ruleName => ruleName !== '@global');
  const generatedClasses = {};
  const activeRules = {};

  const getClassName = ruleName => {
    if (generatedClasses[ruleName]) return generatedClasses[ruleName];
    if (!styleRules[ruleName]) return ruleName;
    if (activeRules[ruleName]) return generatedClasses[ruleName] || ruleName;

    activeRules[ruleName] = true;
    const normalizedStyles = normalizeStyleObject(styleRules[ruleName], ruleName, getClassName);
    generatedClasses[ruleName] = css({
      label: options.name ? `${options.name}-${ruleName}` : ruleName,
      ...normalizedStyles,
    });
    activeRules[ruleName] = false;

    return generatedClasses[ruleName];
  };

  ruleNames.forEach(getClassName);
  return generatedClasses;
};

class StyledComponent extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    className: PropTypes.string,
    WrappedComponent: PropTypes.elementType.isRequired,
  };

  render() {
    const { classes, className = '', WrappedComponent, ...passThroughProps } = this.props;

    return <WrappedComponent classes={classes} className={className} {...passThroughProps} />;
  }
}

const EmotionStyledComponent = ({ WrappedComponent, style, styledOptions, customProps, ...props }) => {
  const theme = useTheme();
  const { styles: styleOverrides, ...otherProps } = props;

  return (
    <ClassNames>
      {({ css }) => {
        const defaultStyles = styles(theme, props, style);
        const mergedStyles = merge({}, defaultStyles, styleOverrides || {});
        const classes = createClasses(css, mergedStyles, styledOptions);
        const globalStyles = mergedStyles['@global'] ? normalizeStyleObject(mergedStyles['@global'], '@global', ruleName => classes[ruleName] || ruleName) : null;

        return (
          <React.Fragment>
            {globalStyles && <Global styles={globalStyles} />}
            <StyledComponent {...customProps} {...otherProps} classes={classes} WrappedComponent={WrappedComponent} />
          </React.Fragment>
        );
      }}
    </ClassNames>
  );
};

EmotionStyledComponent.propTypes = {
  WrappedComponent: PropTypes.elementType.isRequired,
  style: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  styledOptions: PropTypes.object.isRequired,
  customProps: PropTypes.object.isRequired,
  styles: PropTypes.object,
};

const styled = (WrappedComponent, customProps = {}) => {
  return (style, options = {}) => {
    const HOCProps = WrappedComponent => {
      return function _HOCProps(props) {
        return <EmotionStyledComponent {...props} customProps={customProps} styledOptions={options} style={style} WrappedComponent={WrappedComponent} />;
      };
    };
    return HOCProps(WrappedComponent);
  };
};

export default styled;
